import { useState } from 'react';
import { Zap, FileText, Check, X, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useActions, usePreApprovals, useCreatePreApproval, useDeletePreApproval } from '@/hooks/useActions';
import AiInstructionsSection from '@/components/settings/AiInstructionsSection';

const ACTION_GROUPS = [
  {
    id: 'classification', name: 'Email Classification', description: 'Categorize and flag emails',
    actions: ['mark_read', 'set_category', 'flag_email']
  },
  {
    id: 'management', name: 'Email Management', description: 'Delete, archive, and move emails',
    actions: ['delete_email', 'archive_email', 'move_to_folder']
  },
  {
    id: 'rules', name: 'Email Rules', description: 'Create and manage filtering rules',
    actions: ['create_email_rule', 'edit_email_rule', 'delete_email_rule', 'get_email_rules']
  },
  {
    id: 'replies', name: 'Pending Replies', description: 'Track emails requiring responses',
    actions: ['register_pending_reply', 'delete_pending_reply', 'get_pending_replies', 'draft_reply']
  },
  {
    id: 'events', name: 'Calendar Events', description: 'Create and manage calendar events',
    actions: ['create_event', 'update_event', 'delete_event', 'get_upcoming_events']
  },
  {
    id: 'ai_instructions', name: 'AI Instructions', description: 'Manage AI behavior instructions',
    actions: ['add_ai_instruction', 'edit_ai_instruction', 'delete_ai_instruction', 'get_ai_instructions']
  },
  {
    id: 'search', name: 'Search & Read', description: 'Search and retrieve email content',
    actions: ['search_emails', 'read_email']
  },
  {
    id: 'other', name: 'Other Actions', description: 'Miscellaneous actions',
    actions: ['get_historic_prices']
  }
];

function ActionGroup({ group, actions, approvalMap, onApprovalChange, isProcessing }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const groupActions = actions.filter(action => group.actions.includes(action.name));
  if (groupActions.length === 0) return null;

  const enabledActions = groupActions.filter(a => a.enabled);
  const states = enabledActions.map(action => {
    const approval = approvalMap.get(action.name);
    if (!approval) return 'ask';
    return approval.autoApprove ? 'always' : 'never';
  });

  const allSame = states.length > 0 && states.every(s => s === states[0]);
  const groupState = allSame ? states[0] : 'mixed';

  const handleBulkApprove = () => {
    enabledActions.forEach(action => onApprovalChange(action.name, 'always'));
  };

  const handleBulkAsk = () => {
    enabledActions.forEach(action => onApprovalChange(action.name, 'ask'));
  };

  const handleBulkDeny = () => {
    enabledActions.forEach(action => onApprovalChange(action.name, 'never'));
  };

  return (
    <div className="border rounded-lg">
      <div className="p-4 bg-muted/30">
        <div className="flex items-center justify-between">
          <button onClick={() => setIsExpanded(!isExpanded)} className="flex items-center gap-2 flex-1 text-left hover:opacity-80 transition-opacity">
            {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            <div>
              <h3 className="font-medium text-sm">{group.name}</h3>
              <p className="text-xs text-muted-foreground">{group.description}</p>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground mr-2">{enabledActions.length} action{enabledActions.length !== 1 ? 's' : ''}</span>
            <div className="flex gap-1">
              <button onClick={handleBulkApprove} disabled={isProcessing || enabledActions.length === 0}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${groupState === 'always' ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                title="Approve all in group"><Check className="h-3 w-3" /></button>
              <button onClick={handleBulkAsk} disabled={isProcessing || enabledActions.length === 0}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${groupState === 'ask' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                title="Ask for all in group">?</button>
              <button onClick={handleBulkDeny} disabled={isProcessing || enabledActions.length === 0}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${groupState === 'never' ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                title="Deny all in group"><X className="h-3 w-3" /></button>
            </div>
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="p-4 space-y-2">
          {groupActions.map(action => {
            const approval = approvalMap.get(action.name);
            const currentState = !approval ? 'ask' : approval.autoApprove ? 'always' : 'never';
            return (
              <div key={action.name} className="pl-6 py-2 border-l-2 border-muted">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-xs">{action.displayName || action.name}</h4>
                      <Badge variant="outline" className="text-xs">{action.source}</Badge>
                      {action.scope && <Badge variant="secondary" className="text-xs">{action.scope}</Badge>}
                      {!action.enabled && <Badge variant="secondary" className="text-xs">disabled</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">{action.description}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => onApprovalChange(action.name, 'always')} disabled={isProcessing || !action.enabled}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${currentState === 'always' ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                      title="Always auto-approve"><Check className="h-3 w-3" /></button>
                    <button onClick={() => onApprovalChange(action.name, 'ask')} disabled={isProcessing || !action.enabled}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${currentState === 'ask' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                      title="Always ask">?</button>
                    <button onClick={() => onApprovalChange(action.name, 'never')} disabled={isProcessing || !action.enabled}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 ${currentState === 'never' ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                      title="Always reject"><X className="h-3 w-3" /></button>
                  </div>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {currentState === 'always' && <span className="text-green-600 dark:text-green-400">Auto-approve</span>}
                  {currentState === 'ask' && <span className="text-blue-600 dark:text-blue-400">Always ask</span>}
                  {currentState === 'never' && <span className="text-red-600 dark:text-red-400">Always reject</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActionApprovals() {
  const { data: actions, isLoading: actionsLoading, error: actionsError } = useActions(true);
  const { data: preApprovals, isLoading: preApprovalsLoading } = usePreApprovals('user@example.com');
  const createPreApproval = useCreatePreApproval();
  const deletePreApproval = useDeletePreApproval();

  const approvalMap = new Map();
  if (preApprovals && Array.isArray(preApprovals)) {
    preApprovals.forEach(approval => {
      approvalMap.set(approval.action_name, { id: approval.id, autoApprove: approval.auto_approve });
    });
  }

  const handleApprovalChange = async (actionName, newState) => {
    const existing = approvalMap.get(actionName);
    try {
      if (newState === 'always') {
        await createPreApproval.mutateAsync({ actionName, userEmail: 'user@example.com', autoApprove: true });
      } else if (newState === 'never') {
        await createPreApproval.mutateAsync({ actionName, userEmail: 'user@example.com', autoApprove: false });
      } else if (newState === 'ask' && existing?.id) {
        await deletePreApproval.mutateAsync(existing.id);
      }
    } catch (error) {
      console.error('Failed to update approval preference:', error);
    }
  };

  const isLoading = actionsLoading || preApprovalsLoading;
  const isProcessing = createPreApproval.isPending || deletePreApproval.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5" />Action Approvals</CardTitle>
        <CardDescription>Configure approval preferences for AI-suggested actions</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <LoadingSpinner /> : actionsError ? (
          <div className="text-center py-8"><AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" /><p className="text-sm text-red-600">Failed to load actions</p></div>
        ) : !actions || actions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No actions registered</div>
        ) : (
          <div className="space-y-3">
            {ACTION_GROUPS.map(group => (
              <ActionGroup key={group.id} group={group} actions={actions} approvalMap={approvalMap}
                onApprovalChange={handleApprovalChange} isProcessing={isProcessing} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SettingsApprovals() {
  return (
    <div className="space-y-6">
      <AiInstructionsSection />
      <ActionApprovals />
    </div>
  );
}
